import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ExternalLink, Download, FileText, Printer } from "lucide-react";

export default function PdfViewerModal() {
  const [data, setData] = useState(null); // { url, filename }

  useEffect(() => {
    const handler = (e) => setData(e.detail);
    window.addEventListener("clinped:pdf", handler);
    return () => window.removeEventListener("clinped:pdf", handler);
  }, []);

  const close = () => setData(null);

  const handlePrint = () => {
    const iframe = document.getElementById("pdf-iframe");
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      window.open(data.url, "_blank", "noopener,noreferrer");
    }
  };

  const handleDownload = async () => {
    try {
      const res = await fetch(data.url);
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = data.filename || "documento.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    } catch (e) {
      // Fallback: abrir en pestaña nueva para guardar manualmente
      window.open(data.url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <AnimatePresence>
      {data && (
        <motion.div
          data-testid="pdf-viewer"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex flex-col"
          onClick={close}
        >
          <div className="flex items-center justify-between gap-3 p-3 bg-white shadow" onClick={(e) => e.stopPropagation()}>
            <p className="font-outfit font-semibold text-brand-ink text-sm flex items-center gap-2 truncate">
              <FileText size={18} className="text-brand-terracotta shrink-0" />
              <span className="truncate">{data.filename || "Documento PDF"}</span>
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={handlePrint} data-testid="pdf-print"
                className="flex items-center gap-1.5 bg-brand-ink text-white text-xs font-bold px-3 py-2 rounded-xl hover:opacity-90 transition-all">
                <Printer size={14} /> Imprimir
              </button>
              <a href={data.url} target="_blank" rel="noopener noreferrer" data-testid="pdf-open-newtab"
                className="flex items-center gap-1.5 bg-brand-terracotta text-white text-xs font-bold px-3 py-2 rounded-xl hover:bg-brand-terracottaDark transition-all">
                <ExternalLink size={14} /> Abrir
              </a>
              <button onClick={handleDownload} data-testid="pdf-download"
                className="flex items-center gap-1.5 bg-brand-sage text-white text-xs font-bold px-3 py-2 rounded-xl hover:opacity-90 transition-all">
                <Download size={14} /> Descargar
              </button>
              <button onClick={close} data-testid="pdf-close" className="w-9 h-9 rounded-xl bg-stone-100 hover:bg-stone-200 flex items-center justify-center transition-all">
                <X size={18} className="text-brand-ink" />
              </button>
            </div>
          </div>
          <div className="flex-1 p-2 sm:p-4" onClick={(e) => e.stopPropagation()}>
            <iframe
              key={data.url}
              src={data.url}
              title="PDF"
              data-testid="pdf-iframe"
              className="w-full h-full rounded-xl bg-white"
            />
            <p className="text-center text-white/80 text-xs mt-2 sm:hidden">
              ¿No se ve la vista previa? Toca <b>Abrir</b> o <b>Descargar</b> arriba.
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
